import { DetailAvisImmeubleModel, DetailAvisResponseModel, tauxTaxesModel, TraitementAvisModel } from '../types/detailAvisModel'
import { detailAvisFormEnum, detailAvisResponseEnum, enteteTableauAffichageImmeubleLabelEnum, immeubleExterneEnum } from '../enums/detailAvisEnum'

// Frais de taxe spéciale : pour un avis Grand Paris, la cotisation est répartie entre la taxe spéciale
// et la TSGP selon les coefficients de répartition de l'année. Seul calcul des frais TS : il est partagé
// par le formulaire, la cellule du tableau et le total des frais pour que tous affichent la même valeur.
export const calculerFraisTaxeSpeciale = (cotisation: number, taux: tauxTaxesModel, grandParis: boolean): number => {
	if (grandParis) {
		const repartitionTaxeSpeciale = Number(taux[detailAvisResponseEnum.repartitionGpTaxeSpeciale] || 0)
		const repartitionTaxeSpecialeGp = Number(taux[detailAvisResponseEnum.repartitionGpTaxeSpecialeGp] || 0)
		const partTaxeSpecialeGp = repartitionTaxeSpecialeGp / (repartitionTaxeSpeciale + repartitionTaxeSpecialeGp)
		const partTaxeSpeciale = (repartitionTaxeSpeciale / (repartitionTaxeSpeciale + repartitionTaxeSpecialeGp) || 0)
		return partTaxeSpeciale * cotisation * Number(taux[detailAvisResponseEnum.taxeSpeciale] || 0) / 100
			+ partTaxeSpecialeGp * cotisation * Number(taux[detailAvisResponseEnum.taxeSpecialeGp] || 0) / 100
	}

	return cotisation * Number(taux[detailAvisResponseEnum.taxeSpeciale] || 0) / 100
}

// Frais commune calculés par le taux, sans ajustement
const calculerFraisCommuneSansAjustement = (immeuble: DetailAvisImmeubleModel, taux: tauxTaxesModel): number => {
	return parseFloat(((parseFloat(immeuble[detailAvisResponseEnum.communeCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.interCommCotisation] as string)) * taux[detailAvisResponseEnum.commune] / 100).toFixed(2))
}

// Ajustement des frais commune saisi à la validation. Il n'est pas stocké à part : en consultation, on le
// retrouve par différence entre les frais commune enregistrés et le calcul par le taux (le taux commune d'une
// année ne change pas après validation). Borné aux valeurs de la liste déroulante (-5 à +5).
// En validation, les frais enregistrés n'existent pas encore : l'ajustement vaut 0.
export const calculerAjustementFrais = (immeuble: DetailAvisImmeubleModel, taux: tauxTaxesModel): number => {
	const fraisCommune = immeuble[detailAvisResponseEnum.fraisCommune]
	if (fraisCommune === undefined || fraisCommune === null) {
		return 0
	}
	const ajustement = Math.round(parseFloat(fraisCommune as string) - calculerFraisCommuneSansAjustement(immeuble, taux))

	return Math.max(-5, Math.min(5, ajustement)) || 0
}

// INTDEP-67 : calcul du total des frais par formule (cotisation × taux / 100),
// indépendant de avis.frais_de_gestion stocké en BDD (qui vient figé du XML fiscal).
export const calculerTotalFrais = (avis: DetailAvisResponseModel): number => {
	const taux = avis[detailAvisResponseEnum.tauxTaxes]
	return avis[detailAvisResponseEnum.immeubles].reduce((total, immeuble) => {
		const fraisCommune = (parseFloat(immeuble[detailAvisResponseEnum.communeCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.interCommCotisation] as string)) * Number(taux[detailAvisResponseEnum.commune]) / 100 + calculerAjustementFrais(immeuble, taux)
		const fraisDepartement = parseFloat(immeuble[detailAvisResponseEnum.departementCotisation] as string) * Number(taux[detailAvisResponseEnum.departement]) / 100
		const fraisTaxeSpe = calculerFraisTaxeSpeciale(parseFloat(immeuble[detailAvisResponseEnum.taxeSpecialeCotisation] as string), taux, avis[detailAvisResponseEnum.grandParis] ?? false)
		const fraisSyndicat = parseFloat(immeuble[detailAvisResponseEnum.syndicatCotisation] as string) * Number(taux[detailAvisResponseEnum.syndicatCommune]) / 100
		const fraisTeom = parseFloat(immeuble[detailAvisResponseEnum.teomCotisation] as string) * Number(taux[detailAvisResponseEnum.teom]) / 100
		const fraisGemapi = parseFloat(immeuble[detailAvisResponseEnum.gemapiCotisation] as string) * Number(taux[detailAvisResponseEnum.gemapi]) / 100
		return total + fraisCommune + fraisDepartement + fraisTaxeSpe + fraisSyndicat + fraisTeom + fraisGemapi
	}, 0)
}

export const mapperAvisPourTraitement = (avis: DetailAvisResponseModel): TraitementAvisModel => {
	return (
		{
			...avis,
			[detailAvisFormEnum.immeubles]: avis[detailAvisResponseEnum.immeubles].map((immeuble) => (
				{
					[detailAvisResponseEnum.adresse]: immeuble[detailAvisResponseEnum.adresse],
					[detailAvisResponseEnum.immeubleExterne]: {
						[immeubleExterneEnum.id]: 0,
						[immeubleExterneEnum.reference]: immeuble[detailAvisResponseEnum.reference] ?? '',
						[immeubleExterneEnum.adresse]: immeuble[detailAvisResponseEnum.adresse] ?? '',
						[immeubleExterneEnum.ville]: immeuble[detailAvisResponseEnum.ville] ?? ''
					},
					[detailAvisResponseEnum.totalCotisation]: immeuble[detailAvisResponseEnum.totalCotisation],
					[detailAvisFormEnum.ajustementFrais]: calculerAjustementFrais(immeuble, avis[detailAvisResponseEnum.tauxTaxes]).toString(),
					[detailAvisResponseEnum.communeCotisation]: {
						[detailAvisResponseEnum.type]: detailAvisResponseEnum.communeCotisation,
						[detailAvisResponseEnum.caseVide]: enteteTableauAffichageImmeubleLabelEnum.cotisation,
						[detailAvisResponseEnum.communeCotisation]: immeuble[detailAvisResponseEnum.communeCotisation],
						[detailAvisResponseEnum.interCommCotisation]: immeuble[detailAvisResponseEnum.interCommCotisation],
						[detailAvisResponseEnum.departementCotisation]: immeuble[detailAvisResponseEnum.departementCotisation],
						[detailAvisResponseEnum.taxeSpecialeCotisation]: immeuble[detailAvisResponseEnum.taxeSpecialeCotisation],
						[detailAvisResponseEnum.syndicatCotisation]: immeuble[detailAvisResponseEnum.syndicatCotisation],
						[detailAvisResponseEnum.teomCotisation]: immeuble[detailAvisResponseEnum.teomCotisation],
						[detailAvisResponseEnum.gemapiCotisation]: immeuble[detailAvisResponseEnum.gemapiCotisation],
						[detailAvisResponseEnum.total]: parseFloat(immeuble[detailAvisResponseEnum.communeCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.interCommCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.departementCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.taxeSpecialeCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.syndicatCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.teomCotisation] as string) + parseFloat(immeuble[detailAvisResponseEnum.gemapiCotisation] as string)
					},
					[detailAvisResponseEnum.frais]: {
						[detailAvisResponseEnum.type]: detailAvisResponseEnum.frais,
						[detailAvisResponseEnum.caseVide]: enteteTableauAffichageImmeubleLabelEnum.frais,
						[detailAvisResponseEnum.communeCotisation]: (calculerFraisCommuneSansAjustement(immeuble, avis[detailAvisResponseEnum.tauxTaxes]) + calculerAjustementFrais(immeuble, avis[detailAvisResponseEnum.tauxTaxes])).toFixed(2),
						[detailAvisResponseEnum.interCommCotisation]: 0,
						[detailAvisResponseEnum.departementCotisation]: (parseFloat(immeuble[detailAvisResponseEnum.departementCotisation] as string) * avis[detailAvisResponseEnum.tauxTaxes][detailAvisResponseEnum.departement] / 100).toFixed(2),
						[detailAvisResponseEnum.taxeSpecialeCotisation]: calculerFraisTaxeSpeciale(parseFloat(immeuble[detailAvisResponseEnum.taxeSpecialeCotisation] as string), avis[detailAvisResponseEnum.tauxTaxes], avis[detailAvisResponseEnum.grandParis] ?? false).toFixed(2),
						[detailAvisResponseEnum.syndicatCotisation]: (parseFloat(immeuble[detailAvisResponseEnum.syndicatCotisation] as string) * avis[detailAvisResponseEnum.tauxTaxes][detailAvisResponseEnum.syndicatCommune] / 100).toFixed(2),
						[detailAvisResponseEnum.teomCotisation]: (parseFloat(immeuble[detailAvisResponseEnum.teomCotisation] as string) * avis[detailAvisResponseEnum.tauxTaxes][detailAvisResponseEnum.teom] / 100).toFixed(2),
						[detailAvisResponseEnum.gemapiCotisation]: (parseFloat(immeuble[detailAvisResponseEnum.gemapiCotisation] as string) * avis[detailAvisResponseEnum.tauxTaxes][detailAvisResponseEnum.gemapi] / 100).toFixed(2),
						[detailAvisResponseEnum.total]: 0
					},
					[detailAvisResponseEnum.totalLigne]: {
						[detailAvisResponseEnum.type]: detailAvisResponseEnum.totalLigne,
						[detailAvisResponseEnum.caseVide]: enteteTableauAffichageImmeubleLabelEnum.total,
						[detailAvisResponseEnum.communeCotisation]: 0,
						[detailAvisResponseEnum.interCommCotisation]: 0,
						[detailAvisResponseEnum.departementCotisation]: 0,
						[detailAvisResponseEnum.taxeSpecialeCotisation]: 0,
						[detailAvisResponseEnum.syndicatCotisation]: 0,
						[detailAvisResponseEnum.teomCotisation]: 0,
						[detailAvisResponseEnum.gemapiCotisation]: 0,
						[detailAvisResponseEnum.total]: 0
					}
				}
			)),
			[detailAvisFormEnum.grandParis]: avis[detailAvisResponseEnum.grandParis] ?? false,
			[detailAvisResponseEnum.communeCotisation]: {
				[detailAvisResponseEnum.type]: detailAvisResponseEnum.communeCotisation,
				[detailAvisResponseEnum.caseVide]: enteteTableauAffichageImmeubleLabelEnum.cotisation,
				[detailAvisResponseEnum.communeCotisation]: '0',
				[detailAvisResponseEnum.interCommCotisation]: '0',
				[detailAvisResponseEnum.departementCotisation]: '0',
				[detailAvisResponseEnum.taxeSpecialeCotisation]: '0',
				[detailAvisResponseEnum.syndicatCotisation]: '0',
				[detailAvisResponseEnum.teomCotisation]: '0',
				[detailAvisResponseEnum.gemapiCotisation]: '0',
				[detailAvisResponseEnum.total]: '0'

			},
			[detailAvisResponseEnum.frais]: {
				[detailAvisResponseEnum.type]: detailAvisResponseEnum.frais,
				[detailAvisResponseEnum.caseVide]: enteteTableauAffichageImmeubleLabelEnum.frais,
				[detailAvisResponseEnum.communeCotisation]: '0',
				[detailAvisResponseEnum.interCommCotisation]: '0',
				[detailAvisResponseEnum.departementCotisation]: '0',
				[detailAvisResponseEnum.taxeSpecialeCotisation]: '0',
				[detailAvisResponseEnum.syndicatCotisation]: '0',
				[detailAvisResponseEnum.teomCotisation]: '0',
				[detailAvisResponseEnum.gemapiCotisation]: '0',
				[detailAvisResponseEnum.total]: '0'

			},
			[detailAvisResponseEnum.totalLigne]: {
				[detailAvisResponseEnum.type]: detailAvisResponseEnum.totalLigne,
				[detailAvisResponseEnum.caseVide]: enteteTableauAffichageImmeubleLabelEnum.total,
				[detailAvisResponseEnum.communeCotisation]: '0',
				[detailAvisResponseEnum.interCommCotisation]: '0',
				[detailAvisResponseEnum.departementCotisation]: '0',
				[detailAvisResponseEnum.taxeSpecialeCotisation]: '0',
				[detailAvisResponseEnum.syndicatCotisation]: '0',
				[detailAvisResponseEnum.teomCotisation]: '0',
				[detailAvisResponseEnum.gemapiCotisation]: '0',
				[detailAvisResponseEnum.total]: '0'

			}
		}
	)
}